import axios from 'axios';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { ANALYTICS_API_URL, CTS_API_URL } from '../../../../constants/config';
import * as browserStorage from '../../../../misc/BrowserStorageManager';
import { Notification } from '../../../../styledComponents/styledComponents';
import { useQuery } from '@tanstack/react-query';
import { getUnifiedErrorObject } from '../../../../util/ErrorUnifier';
import styled, { keyframes } from 'styled-components';
import StreamMetricsDisplay from './MetricsContainer';
import moment from 'moment';

export const Container = styled.div`
   font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
   background: #f5f5f5;
   min-height: 100vh;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 20px 10%;
   flex-direction: column;
`;
Container.displayName = 'Container';

const slideIn = keyframes`
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

export const PlayerContainer = styled.div`
   background: #ffffff;
   border-radius: 8px;
   box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
   animation: ${slideIn} 0.3s ease-out;
   width: 100%;
   max-width: 100%;
   max-height: 65vh;
`;
PlayerContainer.displayName = 'PlayerContainer';

const PlayerContainerId = 'playerDivId';

const ENV_CONFIG = {
   dev: {
      ctsDomain: 'token-dev.nanostream.cloud',
      serverDomain: 'bintu-play-dev.nanocosmos.de',
      bintuDomain: 'https://bintu-dev-k8s.nanocosmos.de',
   },
   staging: {
      ctsDomain: 'token-staging.nanostream.cloud',
      serverDomain: 'bintu-play-stage.nanocosmos.de',
      bintuDomain: 'https://bintu-staging.nanocosmos.de',
   },
   prod: {
      ctsDomain: 'token.nanostream.cloud',
      serverDomain: 'bintu-play.nanocosmos.de',
      bintuDomain: 'https://bintu.nanocosmos.de',
   },
};

function inferEnvFromCtsUrl(ctsDomain) {
   if (ctsDomain.includes('dev')) return 'dev';
   if (ctsDomain.includes('staging') || ctsDomain.includes('stage')) return 'staging';
   return 'prod';
}

function resolveEnvConfig(env) {
   return ENV_CONFIG[env] ?? ENV_CONFIG[inferEnvFromCtsUrl(CTS_API_URL)];
}

function getPlayerConfig(stream, token, serverDomain, bintuDomain, isMoqEnabled, handleStats = null) {
   return {
      source: {
         // defaults: {
         //    service: 'bintu',
         // },
         general: {
            serverDomain,
         },
         // options: {
         //    adaption: {
         //       rule: 'deviationOfMean2',
         //       downStep: 1,
         //    },
         //    switch: {
         //       method: 'server',
         //       pauseOnError: false,
         //       forcePlay: true,
         //       fastStart: false,
         //       timeout: 20,
         //    },
         // },
         startIndex: 0,
         group: {
            id: stream,
            apiurl: bintuDomain,
            security: {
               jwtoken: token,
            },
         },
      },
      events: {
         onPause: function (e) {
            console.log('Player paused:', e.data.reason);
         },
         onError: function (e) {
            console.log('error => ', e);
         },
         onWarning: function (e) {
            console.warn('Player warning:', e.data.message);
         },
         ...(handleStats ? { onStats: handleStats } : {}),
      },
      playback: {
         autoplay: true,
         automute: true,
         muted: false,
         volume: 0.2,
         enableMediaOverQuic: isMoqEnabled,
      },
      style: { controls: false, width: 'auto', height: 'auto' },
      metrics: {
         accountId: 'nanocosmos3',
         accountKey: 'nc3tb9eq0bnjburu',
         userId: 'demo1',
         eventId: 'analytics-stream-alert-watch',
         statsInterval: 10,
      },
   };
}

function StreamPlayer({ location }) {
   const [player, setPlayer] = useState(null);
   // const [playerStats, setPlayerStats] = useState(null);
   const [token, setToken] = useState(undefined);
   const [stream, setStream] = useState(undefined);
   const [errorMessageGET, setErrorMessageGET] = useState('');
   const [rtmpStats, setRtmpStats] = useState(null);

   const params = Object.fromEntries(new URLSearchParams(location.search));
   const streamParam = params.streams !== undefined && params.streams.length > 0 ? params.streams : '';
   const orga = params.orga ? params.orga : '';
   const envConfig = resolveEnvConfig(params.env);
   const { ctsDomain, serverDomain, bintuDomain } = envConfig;
   const isMoqEnabled = params.useMoq !== undefined ? params.useMoq === 'true' : true;
   const jwtParam = params.jwt ?? null;

   if (!streamParam && errorMessageGET.length === 0) {
      setErrorMessageGET(`No stream provided via URL parameters ( e.g. /streamPlayer?streams=XXXXX-YYYYY).`);
   }

   const accessToken = browserStorage.getJwtToken();
   const isAdmin = browserStorage.getUserInfo().isAdmin;

   const header = {
      'x-access-token': accessToken,
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
      ...(isAdmin && orga ? { 'x-orga-hash': orga } : {}),
   };

   useEffect(() => {
      if (jwtParam) {
         console.log('Set jwt via URL => ');
         const payload = JSON.parse(atob(jwtParam.split('.')[1]));
         console.log('token => ', jwtParam);
         console.log('token jti => ', payload.jti);
         setToken(jwtParam);
         setStream(streamParam);
      }
   }, [jwtParam, streamParam]);

   useQuery({
      queryKey: [streamParam],
      enabled: streamParam.length > 0 && !jwtParam,
      queryFn: () => {
         return axios
            .get(
               `${ANALYTICS_API_URL}/streamWatch/playerConfig?streams=${streamParam}&ctsDomain=${ctsDomain}&bintuDomain=${bintuDomain}`,
               {
                  headers: header,
               },
            )
            .then((res) => res.data);
      },
      onSuccess: (data) => {
         const { token, stream } = data;
         const payload = JSON.parse(atob(token.split('.')[1]));
         console.log('token => ', token);
         console.log('token jti => ', payload.jti);
         setToken(token);
         setStream(stream);
      },
      onError: (error) => {
         const errorObject = getUnifiedErrorObject(error);
         setErrorMessageGET(`Something unexpected happened: ${errorObject.message}`);
         setToken(undefined);
         setStream(undefined);
      },
      refetchOnWindowFocus: false,
      retry: false,
   });

   useQuery({
      queryKey: ['rtmpStats/streamTimeRatio'],
      enabled: stream !== undefined,
      queryFn: () => {
         return axios
            .get(`${ANALYTICS_API_URL}/v2/monitoring/ingest/rtmp/latestMetrics?streams=${streamParam}`, {
               headers: header,
            })
            .then((res) => res.data);
      },
      onSuccess: (data) => {
         if (data.statusCode !== 2000) {
            const { timestamp, metrics } = data.data;
            const { bitrate, maxFrameDelay, streamTimeRatio, elapsedTime } = metrics;
            setRtmpStats({
               timestamp: moment.utc(timestamp * 1000),
               bitrate: bitrate.video.kbits,
               maxFrameDelay: maxFrameDelay.milliseconds,
               streamTimeRatio: streamTimeRatio.ratio,
               elapsedTime: elapsedTime.seconds,
            });
         }
      },
      onError: (error) => {
         // const errorObject = getUnifiedErrorObject(error);
         // console.log('rtmp stats fetching error: ', errorObject.message);
         setRtmpStats(null);
      },
      refetchInterval: 15000,
      refetchOnWindowFocus: false,
   });

   useEffect(() => {
      if (stream) {
         if (window.NanoPlayer && player === null) {
            const playerInstance = new window.NanoPlayer(PlayerContainerId);
            setPlayer(playerInstance);
            const config = getPlayerConfig(stream, token, serverDomain, bintuDomain, isMoqEnabled); // (event) => setPlayerStats(event.data.stats));
            console.log('Player config => ', JSON.stringify(config));
            playerInstance.setup(config).catch((error) => console.error(error));
         }
      }
      return () => {
         if (player) {
            player.destroy().catch((error) => console.error(error));
         }
      };
   }, [stream, token, player, serverDomain, bintuDomain, isMoqEnabled]);

   return (
      <Container>
         <PlayerContainer id={PlayerContainerId} />
         {errorMessageGET && <Notification>{errorMessageGET}</Notification>}
         <StreamMetricsDisplay inputMetrics={rtmpStats} stream={stream} />
      </Container>
   );
}

StreamPlayer.propTypes = {
   location: PropTypes.object,
};

export default StreamPlayer;
